
import { useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { Loader2 } from "lucide-react";

export const PremiumRoute = ({ children }: { children: React.ReactNode }) => {
  const [isPremium, setIsPremium] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(true);
  const [shouldRedirect, setShouldRedirect] = useState(false);
  const location = useLocation();
  const { toast } = useToast();

  useEffect(() => {
    async function checkPremiumStatus() {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        
        if (!user) {
          setIsPremium(false);
          setShouldRedirect(true);
          return;
        }

        const { data, error } = await supabase.rpc('is_premium_user', {
          user_uuid: user.id
        });

        if (error) {
          console.error("Error checking premium status:", error);
          throw error;
        }

        // Update premium status
        const userIsPremium = !!data;
        setIsPremium(userIsPremium);
        
        // If not premium, show toast and set redirect flag
        if (!userIsPremium) {
          toast({
            title: "Premium Feature",
            description: "This feature requires a Premium subscription",
            variant: "destructive",
          });
          setShouldRedirect(true);
        }
      } catch (error: any) {
        console.error("Premium check error:", error);
        toast({
          title: "Error checking subscription",
          description: error.message || "Please try again later",
          variant: "destructive",
        });
        setIsPremium(false);
        setShouldRedirect(true);
      } finally {
        setLoading(false);
      }
    }

    checkPremiumStatus();
  }, [toast, location.pathname]);

  // Use ProtectedRoute component first to ensure authentication
  return (
    <ProtectedRoute>
      {loading ? (
        <div className="flex justify-center items-center min-h-screen">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : isPremium ? (
        <>{children}</>
      ) : shouldRedirect ? (
        <Navigate to="/pricing" replace />
      ) : (
        // Fallback when not redirecting yet
        <div className="flex justify-center items-center min-h-screen">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      )}
    </ProtectedRoute>
  );
};
